<script setup>
// NOTICE: KAPTION-INTEGRATION
// call-transcript-search.WEB.2 / WEB.4 — one call that matched a search:
// line 1 the contact, direction, inbox, date, duration and agents; then up to
// 3 snippets (1 when compact, in global search), each "speaker · 2:14 chip ·
// text with the matched terms highlighted". The chip opens the call detail
// playing from that second (`jump`); the rest of the row opens the detail at
// the top (`open`). Same layout as the desktop app's search hits.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { formatCallTime } from './kaptionCallModel';
import { formatClock } from './kaptionCallTranscriptModel';
import { normalizeSearchResult } from './kaptionCallSearchModel';
import { TRANSCRIPT_ACCENT, TRANSCRIPT_TINT } from './kaptionCallPaper';

const props = defineProps({
  result: { type: Object, required: true },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'jump']);
const { t } = useI18n();

const hit = computed(() => normalizeSearchResult(props.result));
const snippets = computed(() =>
  hit.value.snippets.slice(0, props.compact ? 1 : 3)
);
const meta = computed(() =>
  [
    hit.value.inboxName,
    formatCallTime(hit.value.startedAt),
    hit.value.durationSeconds ? formatClock(hit.value.durationSeconds) : '',
    hit.value.agents.map(a => a.name.split(' ')[0]).join(' → '),
  ]
    .filter(Boolean)
    .join(' · ')
);
const speakerOf = snippet =>
  snippet.isCustomer || !snippet.agentName
    ? t('KAPTION_CALLS.TRANSCRIPT.CUSTOMER')
    : snippet.agentName.split(' ')[0];
</script>

<template>
  <div
    data-testid="kaption-call-search-result"
    :data-call-id="hit.callId"
    role="button"
    tabindex="0"
    class="flex cursor-pointer flex-col gap-1.5 text-left"
    @click="emit('open', hit)"
    @keydown.enter.self="emit('open', hit)"
  >
    <div class="flex items-center gap-2.5">
      <div class="relative shrink-0">
        <Avatar
          :name="hit.contactName || '?'"
          :src="hit.contactAvatar"
          :size="compact ? 24 : 32"
          rounded-full
        />
        <span
          v-if="!compact"
          class="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-n-solid-1 ring-1 ring-n-weak"
        >
          <span
            class="size-2.5 text-n-slate-11"
            :class="
              hit.direction === 'outbound'
                ? 'i-lucide-phone-outgoing'
                : 'i-lucide-phone-incoming'
            "
          />
        </span>
      </div>
      <div class="flex min-w-0 flex-1 flex-col">
        <span class="truncate text-sm font-medium text-n-slate-12">
          {{ hit.contactName }}
        </span>
        <span class="truncate text-xs text-n-slate-11">{{ meta }}</span>
      </div>
    </div>
    <ul
      class="flex flex-col gap-1"
      :class="compact ? 'pl-[2.125rem]' : 'pl-[2.625rem]'"
    >
      <li
        v-for="snippet in snippets"
        :key="snippet.key"
        data-testid="kaption-call-search-snippet"
        class="flex min-w-0 items-baseline gap-1.5 text-xs leading-relaxed"
      >
        <span class="shrink-0 font-semibold" :class="snippet.tone.text">
          {{ speakerOf(snippet) }}
        </span>
        <button
          type="button"
          data-testid="kaption-call-search-jump"
          :data-ms="snippet.startMs"
          class="shrink-0 rounded px-1 py-px font-mono text-[11px] font-medium tabular-nums transition-colors hover:bg-[#F97316]/20"
          :class="[TRANSCRIPT_TINT, TRANSCRIPT_ACCENT]"
          :title="
            $t('KAPTION_CALLS.SEARCH.JUMP', {
              time: formatClock(snippet.startMs / 1000),
            })
          "
          @click.stop="emit('jump', hit, snippet.startMs)"
        >
          {{ formatClock(snippet.startMs / 1000) }}
        </button>
        <span
          class="min-w-0 text-n-slate-11"
          :class="compact ? 'truncate' : 'line-clamp-2'"
        >
          <template v-for="(piece, p) in snippet.pieces" :key="p">
            <mark
              v-if="piece.mark"
              class="rounded-[2px] bg-n-amber-4 px-px font-medium text-n-slate-12"
              >{{ piece.text }}</mark
            ><template v-else>{{ piece.text }}</template>
          </template>
        </span>
      </li>
    </ul>
  </div>
</template>
